import React, { createContext, useContext, useEffect, useState } from "react";
import {
  useAuthenticationClient,
  useCurrentUserRecord,
} from "../authentication/authContext.js";
import { useAPIClient } from "../util/useAPIClient.js";
import { DatabaseManager } from "./databaseManager.js";

// ============================================================================
// Context
// ============================================================================

const DatabaseManagerContext = createContext<DatabaseManager | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface DatabaseManagerProviderProps {
  children: React.ReactNode;
}

export function DatabaseManagerProvider({
  children,
}: DatabaseManagerProviderProps) {
  const authClient = useAuthenticationClient();
  const userRecord = useCurrentUserRecord(authClient);
  const apiClient = useAPIClient(authClient);
  const [databaseManager, setDatabaseManager] = useState<DatabaseManager | null>(
    null,
  );

  // Use user ID as stable dependency instead of full userRecord object
  // This prevents unnecessary DatabaseManager recreation when userRecord object reference changes
  const userID = userRecord?.userID ?? null;

  // Create or close DatabaseManager when user changes
  useEffect(() => {
    // User is signed in (userID is truthy)
    if (userID) {
      const manager = new DatabaseManager(apiClient);
      setDatabaseManager(manager);

      return () => {
        manager.close();
      };
    } else {
      // User signed out or still loading - clear the manager
      // (userID is null when signed out, undefined during initial load)
      setDatabaseManager(null);
    }
  }, [userID, apiClient]);

  return (
    <DatabaseManagerContext.Provider value={databaseManager}>
      {children}
    </DatabaseManagerContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Returns the DatabaseManager instance for the current authenticated user.
 * Returns null if the user is not signed in or if the manager is still initializing.
 */
export function useDatabaseManager(): DatabaseManager | null {
  return useContext(DatabaseManagerContext);
}
